import React, { useState, useCallback, useEffect, createContext, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { X } from 'lucide-react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import OnboardingModal from '../components/OnboardingModal';
import { logger } from '../lib/utils';
import { LoginPage } from './LogInPage';
import { SignUpPage } from './SignUpPage';

export const AuthOverlayContext = createContext({ open: () => {}, close: () => {} });

export const AuthOverlayProvider = ({ children }) => {
	const { user,isLoading } = useAuth();
	const busy = useMemo(()=> isLoading, [isLoading]);
	const location = useLocation();

	const [openState, setOpenState] = useState({ open: false, mode: 'login' });
	const[searchParams] = useSearchParams();
	const [showOnboarding, setShowOnboarding] = useState(false);
	useEffect(()=>{
		if(searchParams.has("authMode")){
			const mode = searchParams.get("authMode");
			if(mode === "login" || mode === "signup"){
				setOpenState({ open: true, mode });
			} 
		}
	},[
		location.pathname,
	])
	const open = useCallback((mode = 'login') => {
		if (user) return;
		setOpenState({ open: true, mode });
	}, [user]);

	const close = useCallback(() => setOpenState({ open: false, mode: 'login' }), []);

	useEffect(() => {
		if (!user) {
			setShowOnboarding(false);
			return;
		}

		const hasProfileDetails =
		!!user.username ||
		!!user.address ||
		!!user.profession ||
		(Array.isArray(user.hobbies) && user.hobbies.length > 0);

		if (openState.open) {
			close();
		}
		logger.info("Checking if onboarding is needed", { user, hasProfileDetails });
		if (!user.isOnboarded && !hasProfileDetails) {
			setShowOnboarding(true);
		} else {
			setShowOnboarding(false);
		}
	}, [user, openState.open, close]);

	const canPromptOnRoute = useCallback(() => {
		const pathname = location.pathname.toLowerCase();

		if (pathname.includes('/upload')) return !busy;
		if(pathname.includes('/callback')) return !busy;
		if(pathname.includes('/forgot-password') || pathname.includes('/reset-password')) return !busy;
		if (pathname === '/') return !busy;

			return true;
		}, [location.pathname, busy]);

	useEffect(() => {
		let timerId;
		if (!user && !openState.open && canPromptOnRoute()) {
			const minMs = 20_000;
			const maxMs = 60_000;
			const delay = Math.floor(Math.random() * (maxMs - minMs + 1)) + minMs;
			timerId = setTimeout(() => {
				if (!user && !openState.open && canPromptOnRoute()) open('login');
			}, delay);
		}
		return () => {
			if (timerId) clearTimeout(timerId);
		};
	}, [user, openState.open, canPromptOnRoute, open]);

	useEffect(() => {
		const onEsc = (e) => {
			if (e.key === 'Escape') close();
		};
		if (openState.open) window.addEventListener('keydown', onEsc);
		return () => window.removeEventListener('keydown', onEsc);
	}, [openState.open, close]);
	const requiresOnboarding = useMemo(()=> Boolean(user) && !user.isOnboarded,[user]);
	return (
		<AuthOverlayContext.Provider value={{ open, close }}>
			{children}
			{openState.open && !user && createPortal(
				<div className="fixed inset-0 z-[1000] bg-black bg-opacity-50 flex items-center justify-center p-4">
					<div className="absolute inset-0 flex items-center justify-center p-4">
						<div className="relative w-max">
							<button
								aria-label="Close"
								onClick={close}
								className="absolute top-8 right-8 p-2 text-gray-600 dark:text-gray-300 dark:hover:text-white"
							>
								<X className="w-4 h-4" />
							</button>
							<div className="rounded-lg max-h-screen overflow-y-auto ">
								<GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
									{openState.mode === 'login' ? <LoginPage /> : <SignUpPage />}
								</GoogleOAuthProvider>
							</div>
						</div>
					</div>
				</div>,
				document.body
			)}

			<OnboardingModal
				open={showOnboarding}
				onClose={() => setShowOnboarding(false)}
				canClose ={requiresOnboarding}
			/>
		</AuthOverlayContext.Provider>
	);
};

export const useAuthOverlay = () => React.useContext(AuthOverlayContext);